<template>
  <div class="dropdown-parent-container interactive-tour-container">
    <button
      type="button"
      title="Select a ChartStyle"
      :id="SELECT_CHART_STYLE_BUTTON"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button sc3-button-white-and-dark-grey sc3-button-control-bar-lower-size"
      :class="{
        'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen,
        'no-chart-style-selected': !chartStyleBeingViewedWithAllChangesSaved
      }"
      ref="chartStyleDropdownMenuButton"
      @click="toggleDropdown()"
    >
      {{ chartStyleBeingViewedWithAllChangesSaved ? chartStyleBeingViewedWithAllChangesSaved.htmlSanitizedName : '-- Select a ChartStyle --' }}
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="chartStyleDropdownMenuButton ? [chartStyleDropdownMenuButton] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="chartStyleDropdownMenuButton"
    >
      <template v-slot:header>
        <div class="dropdown-header-container">
          <span class="dropdown-menu-title">YOUR CHARTSTYLES</span>
          <span
            v-show="!areChartStylesLoading && !hasNoChartStyles && !hasChartStylesLoadFailed && sortedChartStyles.length > 1"
            class="dropdown-menu-title-right-corner-ui-container"
          >
            <SCThreeCheckbox
              id="chart-styles-dropdown-menu-sort-a-to-z-checkbox"
              label-text="A-Z"
              v-model:checkbox-value="areChartStylesSortedAToZ"
              :is-small-font="true"
              :is-circle="true"
              :is-single-spaced="true"
              :is-lato-font="true"
            />
          </span>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="areChartStylesLoading" class="dropdown-loading-body">
          <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
        </div>
        <div v-else-if="hasNoChartStyles" class="dropdown-message-body">
          <span>It looks like you haven't saved any ChartStyles&nbsp;yet!</span>
          <br />
          <span class="dropdown-message-small">
            Save your favorite indicators, overlays and chart settings here
            <br />
            to quickly return to them in the&nbsp;future.
          </span>
          <br />
          <button
            @click="[closeDropdown(), launchSaveChartStyleModal()]"
            type="button"
            class="sc3-button sc3-button-green"
          >Save ChartStyle</button>
        </div>
        <div v-else-if="hasChartStylesLoadFailed" class="dropdown-message-body">
          <span class="dropdown-message-small">Failed to load ChartStyles.</span>
        </div>
        <template v-else>
          <ul>
            <template
              v-for="(chartStyle, index) in sortedChartStyles"
              :key="chartStyle.recid"
            >
              <li v-if="index === chartStylesListDividerLineIndex" class="dropdown-divider"></li>
              <li :class="{ 'selected-dropdown-option': chartStyle.recid === chartStore.recidOfChartStyleBeingViewedWithAllChangesSaved }">
                <span v-if="chartStyle.chartStyleButtonIndex >= 0" class="style-button-color-marker" :style="{ backgroundColor: chartStyle.chartStyleButtonBackgroundColorHexCode }"></span>
                <button
                  type="button"
                  class="ellipsis-overflow"
                  :title="chartStyle.htmlSanitizedName"
                  @click="[closeDropdown(), applyChartStyle(chartStyle)]"
                  v-sanitize-html="chartStyle.name"
                ></button>
                <button
                  type="button"
                  title="Edit ChartStyle Name"
                  class="icon-button icon-on-row-hover-only"
                  @click="[closeDropdown(), launchEditChartStyleNameModal(chartStyle.recid)]"
                ><i class="fas fa-fw fa-pencil-alt blue-icon"></i></button>
                <button
                  type="button"
                  title="Delete ChartStyle"
                  class="icon-button icon-on-row-hover-only"
                  @click="[closeDropdown(), launchDeleteChartStyleModal(chartStyle.recid)]"
                ><i class="fas fa-fw fa-trash-alt red-icon"></i></button>
                <button
                  type="button"
                  title="Set ChartStyle As Your Default"
                  class="icon-button dropdown-menu-option-star-button"
                  :class="{
                    'dropdown-menu-option-icon-button-filled': chartStyle.recid === savedChartsStore.recidOfDefaultChartStyle,
                    'icon-on-row-hover-only': chartStyle.recid !== savedChartsStore.recidOfDefaultChartStyle
                  }"
                  @click="setNewDefaultChartStyle(chartStyle)"
                ><i class="far fa-fw fa-star orange-icon"></i></button>
              </li>
            </template>
          </ul>
        </template>
      </template>
    </DropdownMenu>
    <InteractiveTourWindow
      :page-number="5"
      :position="{ left: (dimensions) => `calc(50% - ${dimensions.baseWidth}px)` }"
      :arrow-position="InteractiveTourTrianglePosition.BottomLeft"
    >
      <template v-slot:main-header>
        <span>Save and access ChartStyles, your customized chart templates</span>
      </template>
      <template v-slot:explanation>
        <div>
          <p>Once you've got your charts set up just the way you like 'em, you can save those settings as a “ChartStyle”. You'll be able to quickly return to that chart template in the future for any symbol.</p>
          <p>The ChartStyles menu here provides access to all of your saved templates, and you can also use the green “Save ChartStyle" button to the right to add a new ChartStyle to your account.</p>
        </div>
      </template>
    </InteractiveTourWindow>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { useModals } from '@/composables/useModals';
  import { InteractiveTourTrianglePosition } from '@/composables/useInteractiveTour';
  import { setDefaultChartStyle } from '@/composables/setDefaultChartStyle';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import InteractiveTourWindow from '@/components/page-skeleton/InteractiveTourWindow.vue';
  import { SimpleFrontendChartStyle } from '@/interfaces/chartStyle';
  import { SELECT_CHART_STYLE_BUTTON } from '@/enums/html-ids';

  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const { loadSimpleFrontendChartStyle } = useLoadChartsAndChartStyles();
  const {
    launchCreateAccountModal,
    launchSaveChartStyleModal,
    launchDeleteChartStyleModal,
    launchEditChartStyleNameModal
  } = useModals();
  const chartStyleDropdownMenuButton = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(chartStyleDropdownMenuButton, { windowResize: true });
  const areChartStylesSortedAToZ = ref<boolean>(false);
  const areChartStylesLoading = computed(() => {
    return !savedChartsStore.hasAttemptedToLoadChartStyles || (!savedChartsStore.chartStyles.length && savedChartsStore.isAttemptingToLoadChartStyles);
  });
  const hasNoChartStyles = computed(() => {
    return !areChartStylesLoading.value && !savedChartsStore.chartStyles.length && !savedChartsStore.hasFailedToLoadChartStyles;
  });
  const hasChartStylesLoadFailed = computed(() => {
    return !areChartStylesLoading.value && !savedChartsStore.chartStyles.length && savedChartsStore.hasFailedToLoadChartStyles;
  });
  const chartStyleBeingViewedWithAllChangesSaved = computed(() => {
    return savedChartsStore.chartStyles.find(chartStyle => chartStyle.recid === chartStore.recidOfChartStyleBeingViewedWithAllChangesSaved) ?? null;
  });
  const sortedChartStyles = computed(() => {
    if (areChartStylesSortedAToZ.value) {
      return savedChartsStore.chartStyles;
    }

    return [
      ...savedChartsStore.chartStyleButtonChartStyles,
      ...savedChartsStore.nonChartStyleButtonChartStyles
    ];
  });
  const chartStylesListDividerLineIndex = computed(() => {
    if (
      areChartStylesSortedAToZ.value ||
      !savedChartsStore.chartStyleButtonChartStyles.length ||
      !savedChartsStore.nonChartStyleButtonChartStyles.length
    ) {
      return -1;
    }

    return savedChartsStore.chartStyleButtonChartStyles.length;
  });

  function toggleDropdown() {
    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  async function applyChartStyle(chartStyle: SimpleFrontendChartStyle) {
    await loadSimpleFrontendChartStyle(chartStyle, { shouldScrollToChartOnSuccess: false });
  }

  async function setNewDefaultChartStyle(chartStyle: SimpleFrontendChartStyle) {
    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    await setDefaultChartStyle(chartStyle);
  }

  watch(isOpen, () => {
    if (!isOpen.value) {
      chartStyleDropdownMenuButton.value?.blur();
    }
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
    width: 100%;
    min-width: 0px;
  }

  .dropdown-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-weight: 900;
    color: #707f8f;
    text-align: left;
    padding: 2px 10px;
    margin: 0px;
    gap: 10px;
  }

  .dropdown-header-container > button {
    display: flex;
    align-items: center;
    margin: 0px;
  }

  .dropdown-loading-body {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
  }

  #select-chart-style-button { /* "select-chart-style-button" matches the value of the SELECT_CHART_STYLE_BUTTON html-id enum */
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  #select-chart-style-button.no-chart-style-selected { /* "select-chart-style-button" matches the value of the SELECT_CHART_STYLE_BUTTON html-id enum */
    color: #707f8f;
  }

  .dropdown-message-body {
    padding: 10px;
    font-weight: bold;
    text-align: center;
  }

  .dropdown-message-body .dropdown-message-small {
    font-size: 14px;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
  }

  .style-button-color-marker {
    position: absolute;
    left: 0px;
    width: 8px;
    height: calc(100% - 1px);
  }
</style>
